import { FileText } from "@phosphor-icons/react";
import LegalLayout, { Section } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      eyebrow="Conditions générales d'utilisation"
      icon={FileText}
      title="Nos conditions, en clair."
      intro="En complétant un questionnaire via Questiomed ou en utilisant l'application en tant que praticien, vous acceptez les présentes conditions générales d'utilisation. Elles complètent la politique de confidentialité et, pour les praticiens souscripteurs, les conditions générales de vente."
      path="/cgu"
    >
      <Section title="1. Définitions">
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Service</strong> : le logiciel en ligne Questiomed accessible à l'adresse questiomed.fr et via ses sous-domaines.</li>
          <li><strong>Éditeur</strong> : l'entité identifiée dans les <a href="/mentions-legales" className="text-[#3A8FA5] underline underline-offset-2">mentions légales</a>.</li>
          <li><strong>Praticien</strong> : professionnel de santé disposant d'un compte pour créer et envoyer des questionnaires.</li>
          <li><strong>Patient</strong> : personne physique invitée par un Praticien à répondre à un questionnaire.</li>
          <li><strong>Cabinet</strong> : ensemble de comptes rattachés à un même praticien administrateur (assistants, secrétariat, associés).</li>
          <li><strong>Contenu</strong> : questionnaires, réponses, scores, notes et documents traités via le Service.</li>
        </ul>
      </Section>

      <Section title="2. Objet du service">
        <p>
          Questiomed est un outil d'aide à la pratique clinique permettant aux professionnels de
          santé de concevoir des questionnaires, de les transmettre à leurs patients par email ou
          par lien sécurisé, et d'exploiter les réponses recueillies (score automatique,
          interprétation, exports, comparaison longitudinale).
        </p>
        <p>
          Le Service ne se substitue pas à un avis, un diagnostic ou une prise en charge médicale
          par un professionnel qualifié. Les scores fournis sont une aide et non une conclusion
          diagnostique.
        </p>
      </Section>

      <Section title="3. Accès au service — Praticiens">
        <p>
          L'accès au Service est réservé aux professionnels de santé exerçant en France ou dans
          l'Union européenne. La création d'un compte suppose la fourniture d'informations
          sincères et l'acceptation des présentes CGU.
        </p>
        <p>
          Une période d'essai gratuit de quinze (15) jours est proposée à chaque nouveau compte.
          À l'issue de cet essai, la poursuite de l'utilisation requiert la souscription d'un
          abonnement conformément aux <a href="/cgv" className="text-[#3A8FA5] underline underline-offset-2">CGV</a>.
        </p>
      </Section>

      <Section title="4. Accès au service — Patients">
        <p>
          Les patients accèdent aux questionnaires via un lien unique et personnel transmis par
          leur praticien. Ils n'ont pas à créer de compte. Avant chaque questionnaire, un écran
          dédié rappelle l'identité du praticien, la nature des données collectées et sollicite
          leur consentement libre, éclairé et univoque (Art. 7-1 RGPD).
        </p>
        <p>
          Les patients s'engagent à répondre de bonne foi, à ne transmettre que des informations
          les concernant, et à ne pas partager leur lien avec un tiers. Ils peuvent à tout moment
          retirer leur consentement, sans que cela ne remette en cause la licéité des traitements
          antérieurs, en s'adressant à leur praticien.
        </p>
      </Section>

      <Section title="5. Compte praticien — Sécurité et confidentialité">
        <p>
          Le praticien est seul responsable de la confidentialité de ses identifiants. Il s'engage
          à choisir un mot de passe robuste, à ne pas le partager, et à activer les mesures de
          sécurité proposées (déconnexion automatique, alertes de connexion). Toute action réalisée
          depuis un compte est réputée effectuée par son titulaire.
        </p>
        <p>
          En cas de perte ou de suspicion d'usage frauduleux, le praticien doit immédiatement
          modifier son mot de passe et en informer l'Éditeur à
          <a href="mailto:contact@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2 ml-1">contact@questiomed.fr</a>.
        </p>
      </Section>

      <Section title="6. Respect du secret médical">
        <p>
          Le praticien reste responsable du secret médical (Art. L. 1110-4 du Code de la santé
          publique) et de la relation de soin. Il s'engage à utiliser Questiomed exclusivement dans
          le cadre légal de son exercice professionnel et à ne collecter que les informations
          strictement nécessaires à la finalité poursuivie (principe de minimisation).
        </p>
      </Section>

      <Section title="7. Cabinet et rôles secondaires">
        <p>
          Le praticien peut inviter des collaborateurs (secrétariat, assistant de recherche
          clinique, associés) à rejoindre son cabinet. Il définit leurs droits d'accès et demeure
          responsable de leurs actions au sein de la plateforme. Les données patients restent
          cloisonnées par cabinet ; l'accès aux données Premium (études cliniques, exports)
          nécessite un abonnement Premium actif porté par le praticien administrateur.
        </p>
      </Section>

      <Section title="8. Contenu — Propriété et licence">
        <p>
          L'ensemble des composants du Service (interfaces, code, algorithmes de scoring,
          bibliothèque de questionnaires validés) est protégé par le droit de la propriété
          intellectuelle et demeure la propriété exclusive de l'Éditeur ou de ses ayants droit.
        </p>
        <p>
          Les questionnaires personnalisés créés par un praticien lui appartiennent. Il concède
          à l'Éditeur, à titre non exclusif et pour la durée strictement nécessaire à l'exécution
          du Service, un droit d'usage sur ces contenus aux fins d'affichage, de sauvegarde et
          de sécurisation.
        </p>
        <p>
          Le praticien s'interdit toute extraction ou réutilisation substantielle de la
          bibliothèque de questionnaires validés hors du cadre de sa pratique clinique
          personnelle.
        </p>
      </Section>

      <Section title="9. Usages interdits">
        <p>Sont notamment interdits :</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>l'utilisation du Service à des fins commerciales tierces, publicitaires ou de démarchage ;</li>
          <li>l'envoi de questionnaires sans le consentement préalable du patient ;</li>
          <li>toute tentative d'atteinte à la sécurité du Service (intrusion, injection, déni de service, ingénierie inverse) ;</li>
          <li>la collecte de données à des fins étrangères à la prise en charge médicale ;</li>
          <li>l'automatisation d'appels à l'API sans autorisation écrite préalable.</li>
        </ul>
      </Section>

      <Section title="10. Disponibilité et maintenance">
        <p>
          L'Éditeur met en œuvre les moyens raisonnables pour assurer un accès continu au Service.
          Il peut cependant procéder à des opérations de maintenance planifiée ou d'urgence,
          susceptibles d'entraîner des interruptions temporaires. Ces interruptions ne pourront
          donner lieu à aucune indemnisation, sauf faute lourde de l'Éditeur.
        </p>
      </Section>

      <Section title="11. Responsabilités">
        <p>
          L'Éditeur agit en qualité de sous-traitant au sens de l'article 28 du RGPD. Il ne peut
          être tenu responsable :
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>de l'interprétation médicale des réponses ou scores, qui relève exclusivement du praticien ;</li>
          <li>du contenu des questionnaires personnalisés créés par un praticien ;</li>
          <li>des conséquences d'un mauvais paramétrage du compte par le praticien ;</li>
          <li>des pertes de données résultant d'un événement extérieur au Service (défaillance du poste de travail du praticien, erreur humaine, cyberattaque tierce).</li>
        </ul>
      </Section>

      <Section title="12. Suspension et résiliation">
        <p>
          En cas de manquement grave aux présentes CGU (usage frauduleux, atteinte à la sécurité,
          violation du secret médical, défaut de paiement), l'Éditeur peut suspendre ou clôturer
          le compte concerné, après notification préalable restée sans effet dans un délai
          raisonnable, sauf urgence.
        </p>
        <p>
          Le praticien peut supprimer son compte à tout moment depuis « Paramètres → Mon compte ».
          Les modalités de restitution des données sont détaillées dans les
          <a href="/cgv" className="text-[#3A8FA5] underline underline-offset-2 ml-1">CGV</a>
          {" "}et la <a href="/confidentialite" className="text-[#3A8FA5] underline underline-offset-2">politique de confidentialité</a>.
        </p>
      </Section>

      <Section title="13. Modification des CGU">
        <p>
          Les présentes conditions peuvent évoluer. Toute modification substantielle sera notifiée
          au moins trente (30) jours à l'avance par email et via l'application. La poursuite
          d'utilisation à l'issue de ce délai vaut acceptation de la nouvelle version.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>
          Pour toute question relative aux présentes conditions :
          <a href="mailto:contact@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2 ml-1">contact@questiomed.fr</a>.
          Pour toute question RGPD :
          <a href="mailto:dpo@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2 ml-1">dpo@questiomed.fr</a>.
        </p>
      </Section>

      <Section title="15. Loi applicable">
        <p>
          Les présentes CGU sont soumises au droit français. Tout litige non résolu à l'amiable
          relève des juridictions françaises compétentes.
        </p>
      </Section>
    </LegalLayout>
  );
}
